/**
 * AgentChat - AI Agent 聊天组件
 * 
 * 架构说明：
 * - AI 可以调用工具，工具分为 backend 和 frontend 两类
 * - backend 工具在服务器执行
 * - frontend 工具由本组件执行，操作UI（如图表）
 * 
 * 当前支持的前端工具：
 * - select_point: 在图表上选择一个点
 * - show_analysis_panel: 展示分析面板
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Input, 
  Button, 
  Typography, 
  Spin,
  message,
  Tag,
  Modal,
  Select,
  Switch
} from 'antd';
import { 
  SendOutlined, 
  RobotOutlined, 
  UserOutlined,
  LoadingOutlined,
  AimOutlined,
  BarChartOutlined,
  SettingOutlined
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';
import { useAppTheme } from '../../theme/ThemeProvider';
import { getConfigAsync } from '../../utils/config';
import { useUsageTracking } from '../../hooks/useUsageTracking';
import { logger } from '../../utils/logger';

const { TextArea } = Input;
const { Text } = Typography;
const { Option } = Select;

const LS_AGENT_PREFS_KEY = 'alphora.agentPrefs.v1';

// ============== 类型定义 ==============

interface ChatMessage {
  role: 'user' | 'agent';
  content: string;
  timestamp: Date;
  toolActions?: ToolAction[];  // 记录执行的工具动作
  analysis?: any;              // 结构化输出（透明度渲染）
}

interface ToolAction {
  tool: string;
  status: 'executing' | 'completed' | 'error';
  result?: any;
}

interface AgentChatProps {
  height?: number | string;
  // 图表控制 API
  chartApiRef?: React.RefObject<any>;
  // K线数据
  ohlcData?: any[];
  // 当前状态
  currentSymbol?: string;
  currentTimeframe?: string;
  // 已选择的点
  selectedPoint?: { time: number; price: number } | null;
  // 回调：选中点变化
  onPointSelect?: (point: { index: number; time: number; price: number } | null) => void;
  // 回调：切换到分析面板
  onShowAnalysisPanel?: (panel: 'point' | 'rectangle') => void;
  // 回调：触发点位分析（等价于点击 Signal Point Analysis 按钮，但不一定切换面板）
  onPointAnalysisAutoRun?: () => void;
  // 回调：触发矩形分析（等价于点击 Rectangle Analysis 的 Start Analysis）
  onRectangleAnalysisAutoRun?: () => void;
  // 回调：设置指标（前端工具），用于让 Agent 选择并打开指标面板
  onSetIndicators?: (args: {
    selected?: string[];
    mode?: 'replace' | 'add' | 'remove';
    placements?: Record<string, 'main' | 'pane'>;
    open_panel?: boolean;
  }) => { success: boolean; result?: any; error?: string };
  // 回调：切换图表上下文（symbol / timeframe / asset type）
  onSetChartContext?: (args: {
    asset_type?: 'crypto' | 'stocks';
    symbol?: string;
    timeframe?: string;
    normalize?: boolean;
    reason?: string;
  }) => { success: boolean; result?: any; error?: string };
  // 回调：推送矩形分析结果到上层（用于面板渲染）
  onRectangleAnalysisResult?: (result: any) => void;
  // 回调：关闭指标面板（在 set_indicators 后自动调用）
  onCloseIndicatorPanel?: () => void;
}


// ============== 前端工具执行器 ==============

interface FrontendToolContext {
  chartApiRef?: React.RefObject<any>;
  ohlcData?: any[];
  onPointSelect?: (point: { index: number; time: number; price: number } | null) => void;
  onShowAnalysisPanel?: (panel: 'point' | 'rectangle') => void;
  onSetIndicators?: (args: {
    selected?: string[];
    mode?: 'replace' | 'add' | 'remove';
    placements?: Record<string, 'main' | 'pane'>;
    open_panel?: boolean;
  }) => { success: boolean; result?: any; error?: string };
  onSetChartContext?: (args: {
    asset_type?: 'crypto' | 'stocks';
    symbol?: string;
    timeframe?: string;
    normalize?: boolean;
    reason?: string;
  }) => { success: boolean; result?: any; error?: string };
}

/**
 * 执行前端工具
 */
const executeFrontendTool = (
  action: { tool: string; args: any },
  context: FrontendToolContext
): { success: boolean; result?: any; error?: string } => {
  
  const { tool, args } = action;
  const { chartApiRef, ohlcData, onPointSelect, onShowAnalysisPanel, onSetIndicators, onSetChartContext } = context;
  
  switch (tool) {
    
    // ========== select_point: 在图表上选择一个点 ==========
    case 'select_point': {
      if (!ohlcData || ohlcData.length === 0) {
        return { success: false, error: '没有K线数据' };
      }
      
      const method = args.method || 'current';
      let targetIndex: number | null = null;
      
      switch (method) {
        case 'current':
          // 当前最新K线
          targetIndex = ohlcData.length - 1;
          break;
          
        case 'by_bars_ago':
          // N根K线之前
          const barsAgo = args.bars_ago || 0;
          targetIndex = Math.max(0, ohlcData.length - 1 - barsAgo);
          break;
          
        case 'by_time':
          // 指定时间
          const targetTime = args.time;
          if (targetTime) {
            const targetTs = new Date(targetTime).getTime() / 1000;
            // 找到最接近的K线
            let minDiff = Infinity;
            ohlcData.forEach((candle, idx) => {
              const candleTs = candle.time || (new Date(candle.datetime).getTime() / 1000);
              const diff = Math.abs(candleTs - targetTs);
              if (diff < minDiff) {
                minDiff = diff;
                targetIndex = idx;
              }
            });
          }
          break;
          
        case 'recent_low':
          // 最近的局部低点
          const lookbackLow = args.lookback || 50;
          const startLow = Math.max(0, ohlcData.length - lookbackLow);
          let minPrice = Infinity;
          for (let i = startLow; i < ohlcData.length; i++) {
            const low = ohlcData[i].low;
            if (low < minPrice) {
              minPrice = low;
              targetIndex = i;
            }
          }
          break;
          
        case 'recent_high':
          // 最近的局部高点
          const lookbackHigh = args.lookback || 50;
          const startHigh = Math.max(0, ohlcData.length - lookbackHigh);
          let maxPrice = -Infinity;
          for (let i = startHigh; i < ohlcData.length; i++) {
            const high = ohlcData[i].high;
            if (high > maxPrice) {
              maxPrice = high;
              targetIndex = i;
            }
          }
          break;
          
        default:
          return { success: false, error: `未知的选点方式: ${method}` };
      }
      
      if (targetIndex === null || targetIndex < 0 || targetIndex >= ohlcData.length) {
        return { success: false, error: '无法找到目标点' };
      }
      
      const targetCandle = ohlcData[targetIndex];
      const time = targetCandle.time || (new Date(targetCandle.datetime).getTime() / 1000);
      const price = targetCandle.close;
      
      // 通知父组件选中了点
      if (onPointSelect) {
        onPointSelect({ index: targetIndex, time, price });
      }
      
      // 滚动图表到该点
      if (chartApiRef?.current?.scrollToTime) {
        chartApiRef.current.scrollToTime(time);
      }
      
      // 激活点工具
      if (chartApiRef?.current?.togglePointTool) {
        chartApiRef.current.togglePointTool();
      }
      
      // 格式化时间
      const datetime = targetCandle.datetime || new Date(time * 1000).toISOString();
      
      return {
        success: true,
        result: {
          index: targetIndex,
          time: datetime,
          price: price,
          message: `已选中 ${datetime.split('T')[0]} 的点，价格 ${price.toFixed(2)}`
        }
      };
    }
    
    // ========== show_analysis_panel: 展示分析面板 ==========
    case 'show_analysis_panel': {
      const panel = args.panel || 'point';
      
      if (onShowAnalysisPanel) {
        onShowAnalysisPanel(panel);
      }
      
      // 滚动到分析面板（如果需要）
      if (args.scroll_to_panel !== false) {
        // 尝试滚动到分析区域
        setTimeout(() => {
          const analysisElement = document.querySelector('[data-analysis-panel]');
          if (analysisElement) {
            analysisElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
      
      return {
        success: true,
        result: {
          panel: panel,
          message: `已切换到${panel === 'point' ? '点位' : '矩形'}分析面板`
        }
      };
    }

    // ========== set_chart_context: 切换图表上下文 ==========
    case 'set_chart_context': {
      if (!onSetChartContext) {
        return { success: false, error: 'Chart context switch is not available.' };
      }
      const res = onSetChartContext(args || {});
      return res.success
        ? { success: true, result: res.result }
        : { success: false, error: res.error || 'Failed to switch chart context' };
    }

    // ========== set_indicators: 选择并打开指标面板 ==========
    case 'set_indicators': {
      if (!onSetIndicators) {
        return { success: false, error: 'Indicator selection is not available.' };
      }
      const res = onSetIndicators(args || {});
      return res.success
        ? { success: true, result: res.result }
        : { success: false, error: res.error || 'Failed to set indicators' };
    }
    
    default:
      logger.warn(`未知的前端工具: ${tool}`);
      return { success: false, error: `未知工具: ${tool}` };
  }
};


// ============== 工具执行状态显示 ==============

const ToolActionDisplay: React.FC<{ actions: ToolAction[], theme: any; t: any }> = ({ actions, theme, t }) => {
  if (!actions || actions.length === 0) return null;
  
  const getToolIcon = (tool: string) => {
    switch (tool) {
      case 'select_point': return <AimOutlined />;
      case 'analyze_point': return <BarChartOutlined />;
      case 'show_analysis_panel': return <BarChartOutlined />;
      case 'set_chart_context': return <BarChartOutlined />;
      case 'select_rectangle': return <BarChartOutlined />;
      case 'analyze_rectangle': return <BarChartOutlined />;
      case 'list_indicators': return <BarChartOutlined />;
      case 'set_indicators': return <BarChartOutlined />;
      default: return <RobotOutlined />;
    }
  };
  
  const getToolName = (tool: string) => {
    switch (tool) {
      case 'select_point': return t('agent.tools.discoverPoint', 'Select point');
      case 'analyze_point': return t('agent.tools.analyzePoint', 'Analyze point');
      case 'show_analysis_panel': return t('agent.tools.showPanel', 'Show panel');
      case 'set_chart_context': return t('agent.tools.setChartContext', 'Switch market');
      case 'select_rectangle': return t('agent.tools.selectRectangleArea', 'Select rectangle');
      case 'analyze_rectangle': return t('agent.tools.analyzeRectangleArea', 'Analyze rectangle');
      case 'list_indicators': return t('agent.tools.listIndicators', 'List indicators');
      case 'set_indicators': return t('agent.tools.setIndicators', 'Set indicators');
      default: return tool;
    }
  };
  
  return (
    <div style={{ marginBottom: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {actions.map((action, idx) => (
        <Tag 
          key={idx}
          icon={getToolIcon(action.tool)}
          color={action.status === 'completed' ? 'success' : action.status === 'error' ? 'error' : 'processing'}
        >
          {getToolName(action.tool)}
        </Tag>
      ))}
    </div>
  );
};


// ============== AgentChat 组件 ==============

const AgentChat: React.FC<AgentChatProps> = ({ 
  height = '100%',
  chartApiRef,
  ohlcData,
  currentSymbol = 'BTCUSDT',
  currentTimeframe = '1h',
  selectedPoint,
  onPointSelect,
  onShowAnalysisPanel,
  onPointAnalysisAutoRun,
  onRectangleAnalysisAutoRun,
  onSetIndicators,
  onSetChartContext,
  onRectangleAnalysisResult,
  onCloseIndicatorPanel,
}) => {
  const { t, i18n } = useTranslation();
  const { theme } = useAppTheme();
  
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [currentToolActions, setCurrentToolActions] = useState<ToolAction[]>([]);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [userCustomInstructions, setUserCustomInstructions] = useState('');
  const [riskProfile, setRiskProfile] = useState<'aggressive' | 'balanced' | 'conservative'>('balanced');
  const [transparencyMode, setTransparencyMode] = useState(true);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const pendingSelectedPointRef = useRef<{ timeSec: number; price: number } | null>(null);
  
  const { checkAndRecordUsage } = useUsageTracking();

  // Load/save user prefs (localStorage)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_AGENT_PREFS_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (typeof parsed?.userCustomInstructions === 'string') setUserCustomInstructions(parsed.userCustomInstructions);
      if (parsed?.riskProfile === 'aggressive' || parsed?.riskProfile === 'balanced' || parsed?.riskProfile === 'conservative') {
        setRiskProfile(parsed.riskProfile);
      }
      if (typeof parsed?.transparencyMode === 'boolean') setTransparencyMode(parsed.transparencyMode);
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(LS_AGENT_PREFS_KEY, JSON.stringify({
        userCustomInstructions,
        riskProfile,
        transparencyMode,
      }));
    } catch (e) {
      // ignore
    }
  }, [userCustomInstructions, riskProfile, transparencyMode]);

  const getApiBaseUrl = async () => {
    const config = await getConfigAsync();
    return config?.API_BASE_URL || 'https://getaws.alpha-pilot.net';
  };

  // Auto scroll
  useEffect(() => {
    // Only scroll the chat container, never the whole page.
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({ top: chatContainerRef.current.scrollHeight, behavior: 'smooth' });
      return;
    }
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, streamingMessage]);

  // 构建上下文 - 包含 ohlc_data
  const buildContext = useCallback(() => ({
    symbol: currentSymbol,
    timeframe: currentTimeframe,
    ohlc_data: ohlcData || [],
    // language passed to backend so agent replies/tool messages match UI language
    language: i18n.language || (window as any).APP_CONFIG?.LANG || (navigator.language || 'zh-CN'),
    selected_point: selectedPoint ? {
      index: ohlcData?.findIndex(c => c.time === selectedPoint.time) ?? -1,
      time: selectedPoint.time,
      price: selectedPoint.price
    } : null
  }), [currentSymbol, currentTimeframe, ohlcData, selectedPoint, i18n.language]);

  const parseTimeToSec = (val: any): number | null => {
    if (val === null || val === undefined) return null;
    if (typeof val === 'number') return val > 1e12 ? Math.floor(val / 1000) : val;
    const n = Number(val);
    if (!Number.isNaN(n)) return n > 1e12 ? Math.floor(n / 1000) : n;
    const ms = new Date(val).getTime();
    if (Number.isNaN(ms)) return null;
    return Math.floor(ms / 1000);
  };

  const findNearestIndexByTime = (targetSec: number, data: any[]): number => {
    if (!Array.isArray(data) || data.length === 0) return -1;
    let bestIdx = -1;
    let bestDiff = Infinity;
    for (let i = 0; i < data.length; i++) {
      const c = data[i];
      const cSec = parseTimeToSec(c?.time ?? c?.datetime);
      if (cSec === null) continue;
      const d = Math.abs(cSec - targetSec);
      if (d < bestDiff) {
        bestDiff = d;
        bestIdx = i;
      }
    }
    return bestIdx;
  };

  // If we received a select_point result for a different market/timeframe, wait until ohlcData updates then apply.
  useEffect(() => {
    const pending = pendingSelectedPointRef.current;
    if (!pending || !ohlcData || ohlcData.length === 0) return;
    const idx = findNearestIndexByTime(pending.timeSec, ohlcData);
    if (idx < 0) return;
    onPointSelect?.({ index: idx, time: pending.timeSec, price: pending.price });
    if (chartApiRef?.current?.scrollToTime) {
      try {
        chartApiRef.current.scrollToTime(pending.timeSec);
      } catch (e) {
        console.error('[AgentChat] scrollToTime error:', e);
      }
    }
    if (chartApiRef?.current?.setPointMarker) {
      chartApiRef.current.setPointMarker(pending.timeSec, pending.price);
    }
    pendingSelectedPointRef.current = null;
  }, [ohlcData, onPointSelect, chartApiRef]);

  // 前端工具执行上下文
  const frontendToolContext: FrontendToolContext = {
    chartApiRef,
    ohlcData,
    onPointSelect,
    onShowAnalysisPanel,
    onSetIndicators,
    onSetChartContext,
  };

  // 处理事件（工具调用、前端动作等）- 供 SSE 和轮询模式共用
  const handleAgentEvent = (
    data: any,
    toolActions: ToolAction[],
    setToolActions: (actions: ToolAction[]) => void
  ) => {
    switch (data.type) {
      case 'tool_call':
        logger.log(`AI 调用工具: ${data.tool}`, data.args);
        if (data.tool === 'analyze_point') {
          onPointAnalysisAutoRun?.();
        }
        if (data.tool === 'set_chart_context') {
          onSetChartContext?.(data.args || {});
        }
        if (data.tool === 'analyze_rectangle') {
          onShowAnalysisPanel?.('rectangle');
        }
        toolActions.push({ tool: data.tool, status: 'executing' });
        setToolActions([...toolActions]);
        break;

      case 'frontend_action':
        logger.log('执行前端动作:', data.action);
        const result = executeFrontendTool(data.action, frontendToolContext);
        logger.log('前端工具执行结果:', result);
        const actionIdx = toolActions.findIndex(a => a.tool === data.action.tool && a.status === 'executing');
        if (actionIdx >= 0) {
          toolActions[actionIdx].status = result.success ? 'completed' : 'error';
          toolActions[actionIdx].result = result.result;
          setToolActions([...toolActions]);
        }
        
        // 对于 set_indicators，成功后延时 1.5 秒关闭指标面板
        if (data.action?.tool === 'set_indicators' && result.success && onCloseIndicatorPanel) {
          setTimeout(() => {
            logger.log('自动关闭指标面板');
            onCloseIndicatorPanel();
          }, 1500);
        }
        break;

      case 'tool_result':
        logger.log(`工具 ${data.tool} 执行完成:`, data.result);
        const toolIdx = toolActions.findIndex(a => a.tool === data.tool && a.status === 'executing');
        if (toolIdx >= 0) {
          toolActions[toolIdx].status = data.result?.success !== false ? 'completed' : 'error';
          toolActions[toolIdx].result = data.result;
          setToolActions([...toolActions]);
        }

        // 处理 select_point
        if (data.tool === 'select_point' && data.result?.success && data.result?.point) {
          const point = data.result.point;
          const timeSec = parseTimeToSec(point.time) ?? null;
          const price = Number(point.price);
          if (timeSec !== null && Number.isFinite(price)) {
            const idx = ohlcData ? findNearestIndexByTime(timeSec, ohlcData) : -1;
            if (idx >= 0 && onPointSelect) {
              onPointSelect({ index: idx, time: timeSec, price });
              if (chartApiRef?.current?.scrollToTime) {
                try { chartApiRef.current.scrollToTime(timeSec); } catch (e) { console.error('[AgentChat] scrollToTime error:', e); }
              }
              if (chartApiRef?.current?.setPointMarker) {
                chartApiRef.current.setPointMarker(timeSec, price);
              }
            } else {
              pendingSelectedPointRef.current = { timeSec, price };
            }
          }
        }

        // 处理 select_rectangle
        if (data.tool === 'select_rectangle' && data.result?.success && data.result?.rectangle) {
          const rect = data.result.rectangle;
          const startIndex = Number(rect.start_index);
          const endIndex = Number(rect.end_index);
          if (Number.isFinite(startIndex) && Number.isFinite(endIndex) && chartApiRef?.current?.setRectangleSelection) {
            chartApiRef.current.setRectangleSelection({ startIndex, endIndex });
            onShowAnalysisPanel?.('rectangle');
            onRectangleAnalysisAutoRun?.();
            if (ohlcData && ohlcData.length > 0 && chartApiRef.current.scrollToTime) {
              const left = Math.max(0, Math.min(startIndex, endIndex));
              const right = Math.min(ohlcData.length - 1, Math.max(startIndex, endIndex));
              const mid = Math.floor((left + right) / 2);
              const midCandle = ohlcData[mid];
              const timeSec = parseTimeToSec(midCandle?.time ?? midCandle?.datetime);
              if (timeSec) {
                try { chartApiRef.current.scrollToTime(timeSec); } catch (e) { console.error('[AgentChat] scrollToTime error:', e); }
              }
            }
          }
        }

        // 处理 analyze_rectangle
        if (data.tool === 'analyze_rectangle' && data.result?.success) {
          onRectangleAnalysisResult?.(data.result);
          onShowAnalysisPanel?.('rectangle');
        }
        break;

      case 'error':
        message.error(data.message);
        break;

      case 'warning':
        message.warning(data.message);
        break;
    }
  };

  // 轮询模式发送消息（适配 API Gateway）
  const handleSendMessagePolling = async () => {
    if (!input.trim()) return;

    const canProceed = await checkAndRecordUsage();
    if (!canProceed) return;

    const userMsg: ChatMessage = {
      role: 'user',
      content: input,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMsg]);
    const messageText = input;
    setInput('');
    setLoading(true);
    setStreamingMessage('');
    setCurrentToolActions([]);

    try {
      const apiBaseUrl = await getApiBaseUrl();
      const { getApiHeaders } = await import('../../utils/apiHeaders');

      const conversationHistory = messages.map(m => ({
        role: m.role === 'agent' ? 'assistant' : 'user',
        content: m.content
      }));

      const ctx = buildContext();

      // 第一步：启动生成任务
      const startResponse = await fetch(`${apiBaseUrl}/agent/chat/start`, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify({
          message: messageText,
          conversation_history: conversationHistory,
          context: ctx,
          user_custom_instructions: userCustomInstructions,
          risk_profile: riskProfile,
          transparency_mode: transparencyMode
        })
      });

      if (!startResponse.ok) throw new Error('Failed to start generation');
      const startData = await startResponse.json();
      const sessionId = startData.session_id;

      if (!sessionId) throw new Error('No session_id returned');

      // 第二步：轮询获取结果
      const toolActions: ToolAction[] = [];
      let latestAnalysis: any = null;
      let agentMessage = '';
      const POLL_INTERVAL = 300; // 300ms
      const MAX_POLLS = 600; // 最多轮询 3 分钟

      for (let i = 0; i < MAX_POLLS; i++) {
        const pollResponse = await fetch(
          `${apiBaseUrl}/agent/chat/poll?session_id=${encodeURIComponent(sessionId)}`,
          { headers: getApiHeaders() }
        );

        if (!pollResponse.ok) {
          if (pollResponse.status === 404) {
            throw new Error('Session expired');
          }
          throw new Error('Poll failed');
        }

        const pollData = await pollResponse.json();

        // 处理新事件
        if (pollData.events && pollData.events.length > 0) {
          for (const event of pollData.events) {
            handleAgentEvent(event, toolActions, setCurrentToolActions);
          }
        }

        // 更新内容
        if (pollData.content && pollData.content !== agentMessage) {
          agentMessage = pollData.content;
          setStreamingMessage(agentMessage);
        }

        // 检查是否完成
        if (pollData.done) {
          if (pollData.error) {
            throw new Error(pollData.error);
          }
          if (pollData.analysis) {
            latestAnalysis = pollData.analysis;
          }
          break;
        }

        // 等待下一次轮询
        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL));
      }

      // 完成：清理并添加最终消息
      setStreamingMessage('');
      setCurrentToolActions([]);

      if (agentMessage) {
        setMessages(prev => [...prev, {
          role: 'agent',
          content: agentMessage,
          timestamp: new Date(),
          toolActions: toolActions.length > 0 ? toolActions : undefined,
          analysis: latestAnalysis || undefined
        }]);
      } else if (latestAnalysis?.summary_markdown) {
        setMessages(prev => [...prev, {
          role: 'agent',
          content: String(latestAnalysis.summary_markdown),
          timestamp: new Date(),
          toolActions: toolActions.length > 0 ? toolActions : undefined,
          analysis: latestAnalysis
        }]);
      }

    } catch (error) {
      console.error('[AgentChat] Polling error:', error);
      logger.error('Agent chat failed:', error);
      message.error(t('agent.chatFailed', '发送失败，请重试'));
    } finally {
      setLoading(false);
    }
  };

  // SSE 模式发送消息（本地开发或直连后端时使用）
  const handleSendMessageSSE = async () => {
    if (!input.trim()) return;

    const canProceed = await checkAndRecordUsage();
    if (!canProceed) return;

    const userMsg: ChatMessage = {
      role: 'user',
      content: input,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);
    setStreamingMessage('');
    setCurrentToolActions([]);

    try {
      const apiBaseUrl = await getApiBaseUrl();
      const { getApiHeaders } = await import('../../utils/apiHeaders');

      const conversationHistory = messages.map(m => ({
        role: m.role === 'agent' ? 'assistant' : 'user',
        content: m.content
      }));

      const ctx = buildContext();
      let latestAnalysis: any = null;

      const response = await fetch(`${apiBaseUrl}/agent/chat`, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify({
          message: input,
          conversation_history: conversationHistory,
          context: ctx,
          user_custom_instructions: userCustomInstructions,
          risk_profile: riskProfile,
          transparency_mode: transparencyMode
        })
      });

      if (!response.ok) throw new Error('Network request failed');

      const reader = response.body?.getReader();
      if (!reader) throw new Error('Cannot read response stream');

      let agentMessage = '';
      const toolActions: ToolAction[] = [];
      const decoder = new TextDecoder();
      let sseBuffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        sseBuffer += decoder.decode(value, { stream: true });
        const events = sseBuffer.split('\n\n');
        sseBuffer = events.pop() || '';

        for (const evt of events) {
          const lines = evt.split('\n');
          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const payload = line.slice(6).trim();
            if (!payload) continue;
            try {
              const data = JSON.parse(payload);

              if (data.type === 'content') {
                agentMessage += data.content;
                setStreamingMessage(agentMessage);
              } else if (data.type === 'analysis') {
                latestAnalysis = data.analysis;
                if (latestAnalysis?.summary_markdown) {
                  agentMessage = String(latestAnalysis.summary_markdown);
                  setStreamingMessage(agentMessage);
                }
              } else {
                // 其他事件类型
                handleAgentEvent(data, toolActions, setCurrentToolActions);
              }
            } catch (e) { /* ignore parse errors */ }
          }
        }
      }

      setStreamingMessage('');
      setCurrentToolActions([]);

      if (agentMessage) {
        setMessages(prev => [...prev, {
          role: 'agent',
          content: agentMessage,
          timestamp: new Date(),
          toolActions: toolActions.length > 0 ? toolActions : undefined,
          analysis: latestAnalysis || undefined
        }]);
      } else if (latestAnalysis?.summary_markdown) {
        setMessages(prev => [...prev, {
          role: 'agent',
          content: String(latestAnalysis.summary_markdown),
          timestamp: new Date(),
          toolActions: toolActions.length > 0 ? toolActions : undefined,
          analysis: latestAnalysis
        }]);
      }

    } catch (error) {
      console.error('[AgentChat] SSE error:', error);
      logger.error('Agent chat failed:', error);
      message.error(t('agent.chatFailed', '发送失败，请重试'));
    } finally {
      setLoading(false);
    }
  };

  // 发送消息 - 根据环境自动选择模式
  // 生产环境使用轮询模式（适配 API Gateway），本地使用 SSE 模式
  // 发送消息 - 统一使用轮询模式（适配 API Gateway）
  // SSE 模式保留在 handleSendMessageSSE 中，如需切换可修改此处
  const handleSendMessage = async () => {
    await handleSendMessagePolling();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div 
      style={{ 
        height,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        padding: '16px',
        background: theme.surfaceBackground,
        color: theme.textPrimary,
        boxSizing: 'border-box'
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
        <RobotOutlined style={{ fontSize: 20, color: theme.accentPrimary }} />
        <Button
          type="text"
          size="small"
          icon={<SettingOutlined />}
          onClick={() => setPrefsOpen(true)}
          style={{ color: theme.textSecondary }}
        />
        <Tag style={{ marginInlineStart: 4 }}>
          {riskProfile === 'aggressive'
            ? t('agent.aggressive', 'Aggressive')
            : riskProfile === 'conservative'
              ? t('agent.conservative', 'Conservative')
              : t('agent.balanced', 'Balanced')}
        </Tag>
        {transparencyMode && (
          <Tag color="blue">
            {t('agent.transparency', 'Transparency')}
          </Tag>
        )}
        <Text type="secondary" style={{ fontSize: 12, marginLeft: 'auto' }}>
          {currentSymbol} · {currentTimeframe}
        </Text>
      </div>

      {/* Messages Area */}
      <div 
        ref={chatContainerRef}
        style={{ 
          flex: 1, 
          overflowY: 'auto',
          marginBottom: 12,
          minHeight: 0
        }}
      >
        {messages.length === 0 ? (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            opacity: 0.7
          }}>
            <RobotOutlined style={{ fontSize: 48, marginBottom: 16, color: theme.textSecondary }} />
            <Text type="secondary" style={{ textAlign: 'center', marginBottom: 8 }}>
              {t('agent.emptyHint')}
            </Text>
            <Text type="secondary" style={{ textAlign: 'center', fontSize: 12 }}>
              {t('agent.emptyExample')}
            </Text>
          </div>
        ) : (
          <>
            {messages.map((msg, index) => (
              <div 
                key={index}
                style={{ 
                  marginBottom: 16,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start'
                }}
              >
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start',
                  gap: 8,
                  maxWidth: '90%',
                  flexDirection: msg.role === 'user' ? 'row-reverse' : 'row'
                }}>
                  <div style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: msg.role === 'user' ? theme.accentPrimary : theme.surfaceBorder,
                    flexShrink: 0
                  }}>
                    {msg.role === 'user' ? 
                      <UserOutlined style={{ fontSize: 14, color: '#fff' }} /> : 
                      <RobotOutlined style={{ fontSize: 14, color: theme.textPrimary }} />
                    }
                  </div>
                  
                  <div style={{
                    padding: '10px 14px',
                    borderRadius: 12,
                    background: msg.role === 'user' ? theme.accentPrimary : theme.toolbarBackground,
                    color: msg.role === 'user' ? '#fff' : theme.textPrimary
                  }}>
                    {/* 显示工具执行状态 */}
                    {msg.toolActions && <ToolActionDisplay actions={msg.toolActions} theme={theme} t={t} />}
                    
                    <div className="markdown-content" style={{ fontSize: 14, lineHeight: 1.6 }}>
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                    </div>

                    {/* Structured transparency panel (if provided by backend) */}
                    {msg.role === 'agent' && msg.analysis && (
                      <details style={{ marginTop: 10 }}>
                        <summary style={{ cursor: 'pointer', color: theme.textSecondary, fontSize: 12 }}>
                          {t('agent.transparency', 'Transparency')}
                        </summary>
                        <div style={{ marginTop: 8, fontSize: 12, color: theme.textSecondary }}>
                          {Array.isArray(msg.analysis?.transparency?.evidence?.bullets) && msg.analysis.transparency.evidence.bullets.length > 0 && (
                            <>
                              <div style={{ marginBottom: 6, color: theme.textPrimary }}>
                                <strong>{t('agent.evidence', 'Evidence')}</strong>
                              </div>
                              <ul style={{ margin: 0, paddingLeft: 18 }}>
                                {msg.analysis.transparency.evidence.bullets.slice(0, 8).map((b: string, i: number) => (
                                  <li key={i}>{b}</li>
                                ))}
                              </ul>
                            </>
                          )}

                          {Array.isArray(msg.analysis?.transparency?.assumptions) && msg.analysis.transparency.assumptions.length > 0 && (
                            <>
                              <div style={{ marginTop: 10, marginBottom: 6, color: theme.textPrimary }}>
                                <strong>{t('agent.assumptions', 'Assumptions')}</strong>
                              </div>
                              <ul style={{ margin: 0, paddingLeft: 18 }}>
                                {msg.analysis.transparency.assumptions.slice(0, 6).map((b: string, i: number) => (
                                  <li key={i}>{b}</li>
                                ))}
                              </ul>
                            </>
                          )}

                          {Array.isArray(msg.analysis?.transparency?.risks) && msg.analysis.transparency.risks.length > 0 && (
                            <>
                              <div style={{ marginTop: 10, marginBottom: 6, color: theme.textPrimary }}>
                                <strong>{t('agent.risks', 'Risks')}</strong>
                              </div>
                              <ul style={{ margin: 0, paddingLeft: 18 }}>
                                {msg.analysis.transparency.risks.slice(0, 6).map((b: string, i: number) => (
                                  <li key={i}>{b}</li>
                                ))}
                              </ul>
                            </>
                          )}

                          {Array.isArray(msg.analysis?.transparency?.evidence?.tool_citations) && msg.analysis.transparency.evidence.tool_citations.length > 0 && (
                            <>
                              <div style={{ marginTop: 10, marginBottom: 6, color: theme.textPrimary }}>
                                <strong>{t('agent.toolsUsed', 'Tools used')}</strong>
                              </div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {msg.analysis.transparency.evidence.tool_citations.slice(0, 8).map((c: any, i: number) => (
                                  <Tag key={i}>
                                    {String(c?.tool || 'tool')}
                                  </Tag>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      </details>
                    )}
                  </div>
                </div>
              </div>
            ))}
            
            {/* Streaming message */}
            {(loading || streamingMessage) && (
              <div style={{ 
                marginBottom: 16,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8
              }}>
                <div style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: theme.surfaceBorder,
                  flexShrink: 0
                }}>
                  <RobotOutlined style={{ fontSize: 14, color: theme.textPrimary }} />
                </div>
                
                <div style={{
                  padding: '10px 14px',
                  borderRadius: 12,
                  background: theme.toolbarBackground,
                  maxWidth: '85%'
                }}>
                  {/* 显示当前正在执行的工具 */}
                  {currentToolActions.length > 0 && (
                    <ToolActionDisplay actions={currentToolActions} theme={theme} t={t} />
                  )}
                  
                  {streamingMessage ? (
                    <div className="markdown-content" style={{ fontSize: 14, lineHeight: 1.6 }}>
                      <ReactMarkdown>{streamingMessage}</ReactMarkdown>
                    </div>
                  ) : (
                    <Spin size="small" />
                  )}
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input Area */}
      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
        <TextArea
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={t('agent.inputPlaceholder', '输入你的问题...')}
          autoSize={{ minRows: 1, maxRows: 3 }}
          disabled={loading}
          style={{ flex: 1, borderRadius: 8, resize: 'none' }}
        />
        <Button
          type="primary"
          icon={loading ? <LoadingOutlined /> : <SendOutlined />}
          onClick={handleSendMessage}
          disabled={loading || !input.trim()}
          style={{ height: 'auto', minHeight: 32, borderRadius: 8 }}
        />
      </div>

      {/* Preferences modal */}
      <Modal
        open={prefsOpen}
        onCancel={() => setPrefsOpen(false)}
        onOk={() => setPrefsOpen(false)}
        title={t('agent.settings', 'Agent settings')}
        okText={t('common.ok', 'OK')}
        cancelText={t('common.cancel', 'Cancel')}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Text style={{ width: 140 }}>{t('agent.riskProfile', 'Risk profile')}</Text>
            <Select value={riskProfile} onChange={v => setRiskProfile(v)} style={{ flex: 1 }}>
              <Option value="conservative">{t('agent.conservative', 'Conservative')}</Option>
              <Option value="balanced">{t('agent.balanced', 'Balanced')}</Option>
              <Option value="aggressive">{t('agent.aggressive', 'Aggressive')}</Option>
            </Select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Text style={{ width: 140 }}>{t('agent.transparencyMode', 'Transparency')}</Text>
            <Switch checked={transparencyMode} onChange={v => setTransparencyMode(v)} />
          </div>

          <div>
            <Text style={{ display: 'block', marginBottom: 6 }}>
              {t('agent.customInstructions', 'Custom instructions')}
            </Text>
            <TextArea
              value={userCustomInstructions}
              onChange={e => setUserCustomInstructions(e.target.value)}
              autoSize={{ minRows: 3, maxRows: 6 }}
              placeholder={t('agent.customInstructionsPlaceholder', 'E.g. be more aggressive, focus on breakouts, keep it concise…')}
            />
            <Text type="secondary" style={{ display: 'block', marginTop: 6, fontSize: 12 }}>
              {t('agent.customInstructionsNote', 'Note: preferences only; cannot override safety/guarantee-profit rules.')}
            </Text>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AgentChat;
